import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import {
  supabase,
  signOutAndReset,
  getRememberMe,
  setRememberMeOn,
  clearRememberMe,
  saveSession,
  loadSavedSession,
} from '../lib/api/supabase';

const AuthContext = createContext(null);
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

// Roles that are allowed to access the CRM.
// Storefront customers (e.g. role='customer' or any other non-CRM role) are denied.
const CRM_ALLOWED_ROLES = ['admin', 'user', 'staff'];

export const AuthProvider = ({ children }) => {
  const [user,    setUser   ] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const mountedRef = useRef(true);

  // ── helpers ──────────────────────────────────────────────────────────────
  const resetAuthState = () => {
    setUser(null); setProfile(null); setIsAdmin(false);
    localStorage.removeItem('user_role');
  };

  /**
   * Fetch CRM profile for uid.
   * Returns the profile data if the user is a valid CRM user.
   * Throws an Error with a user-friendly message if:
   *   - No profile row found (they registered on the storefront, not the CRM)
   *   - Profile role is not a CRM role (e.g. role='customer')
   */
  const fetchProfile = async (uid) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, role, email, full_name')
      .eq('id', uid)
      .maybeSingle();

    if (error) {
      console.error('[Auth] profile fetch error:', error);
      throw new Error('Failed to load profile. Please try again.');
    }

    if (!data) {
      // No CRM profile — this is a storefront customer account
      throw new Error('Access denied. This account is not authorised to access the CRM.');
    }

    if (!CRM_ALLOWED_ROLES.includes(data.role)) {
      // Profile exists but role is not a CRM role (e.g. 'customer')
      throw new Error(`Access denied. Your account role "${data.role}" does not have CRM access.`);
    }

    return data; // valid CRM user
  };

  // Apply a verified profile to state
  const applyProfile = (data) => {
    if (!mountedRef.current) return;
    const admin = data.role === 'admin';
    setProfile(data);
    setIsAdmin(admin);
    localStorage.setItem('user_role', data.role || '');
    console.log('[Auth] ✅ CRM access granted:', data.email, '| role:', data.role);
  };

  // ── Bootstrap ─────────────────────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;
    let unsubscribe = () => {};

    const init = async () => {
      try {
        // Step 1: Restore Remember Me session if active
        if (getRememberMe()) {
          const stored = loadSavedSession();
          if (stored?.access_token && stored?.refresh_token) {
            console.log('[Auth] Remember Me: restoring session...');
            const { data: setData, error: setErr } = await supabase.auth.setSession({
              access_token:  stored.access_token,
              refresh_token: stored.refresh_token,
            });
            if (setErr || !setData.session) {
              console.warn('[Auth] Remember Me restore failed:', setErr?.message);
              clearRememberMe();
            } else {
              saveSession(setData.session); // update rotated token
              console.log('[Auth] Remember Me session restored');
            }
          } else {
            clearRememberMe();
          }
        }

        // Step 2: Get current in-memory session
        const { data: { session } } = await supabase.auth.getSession();
        const u = session?.user ?? null;

        if (!mountedRef.current) return;

        if (u) {
          try {
            const profileData = await fetchProfile(u.id);
            setUser(u);
            applyProfile(profileData);
          } catch (accessErr) {
            // User authenticated but not allowed in CRM — sign them out silently
            console.warn('[Auth] CRM access denied on restore:', accessErr.message);
            clearRememberMe();
            resetAuthState();
            await supabase.auth.signOut();
          }
        } else {
          resetAuthState();
        }
      } catch (err) {
        console.error('[Auth] init error:', err);
        resetAuthState();
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };

    // Subscribe for runtime events (post-init token refreshes)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (loading) return; // init() manages state during startup

        console.log('[Auth] onAuthStateChange:', event, session?.user?.email ?? 'no user');

        if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session && getRememberMe()) {
          saveSession(session);
        }
        if (event === 'SIGNED_OUT') {
          clearRememberMe();
        }

        if (!mountedRef.current) return;
        const u = session?.user ?? null;
        if (u) {
          localStorage.removeItem('user_role');
          try {
            const profileData = await fetchProfile(u.id);
            setUser(u);
            applyProfile(profileData);
          } catch {
            resetAuthState();
            await supabase.auth.signOut();
          }
        } else {
          resetAuthState();
          setUser(null);
        }
      }
    );
    unsubscribe = () => subscription.unsubscribe();

    init();

    return () => { mountedRef.current = false; unsubscribe(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── signIn ────────────────────────────────────────────────────────────────
  const signIn = async (email, password, rememberMe = false) => {
    console.log('[Auth] signIn', { email, rememberMe });

    // Step 1: Authenticate with Supabase
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;

    const u = data.session?.user;
    if (!u) throw new Error('Sign in failed. Please try again.');

    // Step 2: Check CRM access BEFORE accepting the login
    let profileData;
    try {
      profileData = await fetchProfile(u.id);
    } catch (accessErr) {
      // Authenticated but not a CRM user — sign them back out immediately
      await supabase.auth.signOut();
      throw accessErr; // Re-throw so login.jsx shows the error message
    }

    // Step 3: Save Remember Me tokens if requested
    if (rememberMe && data.session) {
      setRememberMeOn();
      saveSession(data.session);
    } else {
      clearRememberMe();
    }

    // Step 4: Apply state
    setUser(u);
    applyProfile(profileData);

    console.log('[Auth] Signed in ok');
    return data;
  };

  // ── signOut ───────────────────────────────────────────────────────────────
  const signOut = async () => {
    console.log('[Auth] signOut');
    resetAuthState();
    setUser(null);
    sessionStorage.clear();
    await signOutAndReset(); // clears RM keys, calls supabase.auth.signOut, redirects
  };

  return (
    <AuthContext.Provider value={{ user, profile, isAdmin, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
